import React, { useEffect, useState } from 'react';
import { FiPlus, FiMoreHorizontal } from 'react-icons/fi';
import { useParams } from 'react-router-dom';
import dragAndDrop from '@/assets/images/icons/drag-and-drop.svg';
import { CourseManageCardLesson } from './course-manage-card-lesson';
import toast from 'react-hot-toast';
import { useClient } from 'jsonapi-react';
import { useFormik } from 'formik';
import { confirm } from '@/lib/confirm';
import { useNavigate } from 'react-router-dom';
import { EmptyState } from '@/components/custom/empty-state';
import { Loader } from '@/components/custom/loader';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { t } from 'i18next';
import usePermission from '@/hooks/usePermission';
import { ManageSubjects, ManageSubjectsContent, ManageSubjectsSidebar } from '@/components/custom/manage';
import { FormField } from '@/components/custom/form';
import { Button, ButtonIcon } from '@/components/ui/button';
import { Sheet, SheetBody, SheetContent, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { FieldGroup } from '@/components/ui/field';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Separator } from '@/components/ui/separator';
import { TaxonomiesIcon, TaxonomiesItem, TaxonomiesText } from '@/components/custom/taxonomies';

export function CourseManageTabSubjects(props) {
  const navigate = useNavigate();
  const { moduleData, course, currentModuleId, filteredDisciplines, setFilteredDisciplines, loading } = props;
  const client = useClient();
  const { disciplineId, moduleId } = useParams();
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editShow, setEditShow] = useState(false);
  const [selectedDisciplineId, setSelectedDisciplineId] = useState();
  const [lessons, setLessons] = useState([]);
  const [click, setClick] = useState(0);
  const [editPath, setEditPath] = useState(false);
  const [editedDiscipline, setEditedDiscipline] = useState();
  const [loadingLessons, setLoadingLessons] = useState(false);

  const getDisciplineLessons = async () => {
    setLoadingLessons(true);
    const { data, error } = await client.fetch(`learning_system_items?filter[learning_system_content_id]=${disciplineId}`);
    if (error) {
      toast.error('Erro ao buscar aulas');
    } else {
      setLessons(data);
    }
    setLoadingLessons(false);
  };

  const handleSubmit = async () => {
    const url = editPath ? ['/learning_system_contents', editedDiscipline.id] : '/learning_system_contents';

    const { error, data } = await client.mutate(url, {
      body: disciplineForm.values.body,
      learningSystemId: currentModuleId
    });
    if (error) {
      editPath ? toast.error('Falha ao editar disciplina') : toast.error('Falha ao criar disciplina');
    } else {
      if (!editPath) {
        toast.success('Disciplina criada com sucesso!');
        filteredDisciplines.push({ ...data });
        disciplineForm.setValues({
          body: '',
          learningSystemId: currentModuleId
        });
      } else {
        toast.success('Disciplina editada com sucesso!');
        const newDisciplines = filteredDisciplines.map(item => (item.id === data.id ? data : item));
        setFilteredDisciplines(newDisciplines);
        setEditPath(false);
      }
    }
  };

  const disciplineForm = useFormik({
    initialValues: {
      body: ''
    },
    onSubmit: handleSubmit
  });

  const handleDelete = async id => {
    const confirmed = await confirm({
      title: t('projects.warningDeleteMatter'),
      description: t('projects.warningDeleteCannotBeUndoneText'),
      confirmText: t('button.yes'),
      cancelText: t('button.no')
    });
    if (confirmed) {
      deleteDiscipline(id);
    }
  };

  const deleteDiscipline = async id => {
    const { error } = await client.delete(`learning_system_contents/${id}`);
    if (error) {
      toast.error('Erro ao excluir disciplina!');
    } else {
      toast.success('Disciplina excluída com sucesso!');
      setFilteredDisciplines(prevState => prevState.filter(item => item.id !== id));
    }
  };

  const handleEdit = discipline => {
    setEditShow(true);
    setEditPath(true);
    setEditedDiscipline(discipline);
  };

  const handleLinkClick = () => {
    const navigationProps = {
      state: { courseId: course.id, moduleId: currentModuleId, disciplineId: disciplineId }
    };

    navigate(`/lms/criar/aula/${disciplineId}/nome/${course?.id}`, navigationProps);
  };

  const reorder = (list, startIndex, endIndex) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);

    return result;
  };

  const updateLessonList = async values => {
    setLoadingLessons(true);
    const { destination, source } = values;
    if (!destination) {
      return null;
    }
    if (destination.index === source.index) {
      return null;
    }

    const dIndex = destination.index;
    const sIndex = source.index;
    const newItems = reorder(lessons, sIndex, dIndex);

    const newLessonsOrder = newItems.map((lesson, index) => {
      return { id: lesson.id, order: index };
    });

    const { error } = await client.mutate(
      ['learning_systems', currentModuleId, 'sort'],
      {
        ['learning-system-items']: newLessonsOrder
      },
      { method: 'PUT' }
    );
    if (error) {
      toast.error('Erro ao reordenar as Aulas!');
    } else {
      getDisciplineLessons();
      toast.success('Aulas reordenadas com sucesso!');
    }
  };

  useEffect(() => {
    setSelectedDisciplineId(window?.history?.state?.disciplineId);
  }, [click, currentModuleId]);

  useEffect(() => {
    if (moduleData?.['learning-system-contents'].length === 0) {
      setLessons([]);
    }

    const moduleById = course?.['learning-systems'].find(item => item?.id == moduleId);
    if (moduleById?.learning_system_items_total_count > 0 && filteredDisciplines?.[0]?.id) {
      navigate(`/curso/admin/gerenciar/${course?.id}/${moduleId}/disciplinas-e-aulas/${filteredDisciplines?.[0]?.id}`);
    }
  }, [filteredDisciplines, moduleData]);

  useEffect(() => {
    const filteredDisciplines = moduleData?.['learning-system-contents']?.filter(item => {
      if (searchTerm == '') {
        return true;
      } else if (item.body.toLowerCase().includes(searchTerm.toLowerCase())) {
        return true;
      }
    });

    setFilteredDisciplines(filteredDisciplines);
  }, [moduleData, searchTerm]);

  useEffect(() => {
    disciplineForm.setValues({
      body: editedDiscipline?.body
    });
  }, [editedDiscipline]);

  useEffect(() => {
    if (disciplineId !== 'd' && disciplineId !== null) {
      getDisciplineLessons();
    }
  }, [disciplineId]);

  return currentModuleId !== 'm' ? (
    <ManageSubjects className="animate-fade-in">
      <ManageSubjectsSidebar>
        <FormField
          label="Disciplinas"
          placeholder={t('textsCommon.research')}
          type="search"
          onChange={e => setSearchTerm(e.target.value)}
          className="mb-6"
        />

        <Button
          variant="outline-dashed"
          size="sm"
          fullWidth
          onClick={() => setShowModal(true)}
        >
          <ButtonIcon>
            <FiPlus />
          </ButtonIcon>
          {t('button.newDiscipline')}
        </Button>

        <Separator className="my-8" />

        {loading && <Loader />}

        {!loading && (
          <Droppable droppableId="droppable">
            {provided => (
              <div
                ref={provided.innerRef}
                {...provided.droppableProps}
              >
                {filteredDisciplines?.map((discipline, i) => (
                  <Draggable
                    key={i}
                    draggableId={discipline.id.toString()}
                    index={i}
                  >
                    {provided => (
                      <TaxonomiesItem
                        onClick={() => {
                          setClick(prevState => prevState + 1);
                          navigate(`/curso/admin/gerenciar/${course?.id}/${currentModuleId}/disciplinas-e-aulas/${discipline.id}`);
                        }}
                        active={parseInt(disciplineId) === discipline.id}
                        key={discipline.id}
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                      >
                        <TaxonomiesIcon
                          src={dragAndDrop}
                          alt=""
                        />

                        <TaxonomiesText>{discipline.body}</TaxonomiesText>

                        <DropdownMenu>
                          <DropdownMenuTrigger className="inline-flex">
                            <FiMoreHorizontal size={16} />
                          </DropdownMenuTrigger>

                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onSelect={() => handleEdit(discipline)}>{t('button.edit')}</DropdownMenuItem>
                            <DropdownMenuItem
                              onSelect={() => handleDelete(discipline.id)}
                              className="text-danger"
                            >
                              {t('button.delete')}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TaxonomiesItem>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        )}
      </ManageSubjectsSidebar>

      <DragDropContext onDragEnd={values => updateLessonList(values)}>
        <ManageSubjectsContent>
          {usePermission('Criar módulos, disciplinas e aulas', 'Cursos') && disciplineId !== 'd' ? (
            <Button
              variant="outline-dashed"
              size="sm"
              fullWidth
              onClick={handleLinkClick}
              className="mb-4"
            >
              <ButtonIcon>
                <FiPlus />
              </ButtonIcon>
              {t('button.createLesson')}
            </Button>
          ) : null}

          {loadingLessons && <Loader />}

          {!loadingLessons && lessons.length === 0 && <EmptyState type="data" />}

          {!loadingLessons && (
            <Droppable droppableId="droppable">
              {provided => (
                <div
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                >
                  <div className="tab__cards tab__cards--h">
                    {lessons.length > 0 &&
                      lessons?.map((lesson, i) => {
                        return (
                          <Draggable
                            key={i}
                            draggableId={lesson.id.toString()}
                            index={i}
                          >
                            {provided => (
                              <div
                                key={i}
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                              >
                                <CourseManageCardLesson
                                  courseId={course?.id}
                                  coursePublished={course?.published}
                                  modulePublished={moduleData?.publish}
                                  setLessons={setLessons}
                                  selectedDisciplineId={disciplineId}
                                  currentModuleId={currentModuleId}
                                  key={i}
                                  lesson={lesson}
                                />
                              </div>
                            )}
                          </Draggable>
                        );
                      })}
                  </div>
                </div>
              )}
            </Droppable>
          )}
        </ManageSubjectsContent>
      </DragDropContext>

      <Sheet
        open={showModal}
        onOpenChange={() => setShowModal(false)}
      >
        <SheetContent>
          <SheetHeader />

          <SheetBody>
            <form
              className="form"
              onSubmit={e => {
                e.preventDefault();
                setShowModal(false);
                disciplineForm.handleSubmit();
              }}
            >
              <FieldGroup>
                <SheetTitle>{t('button.newDiscipline')}</SheetTitle>

                <FormField
                  label={t('textsCommon.disciplineName')}
                  type="text"
                  placeholder={t('textsCommon.writeHere')}
                  onChange={e => disciplineForm.setFieldValue('body', e.target.value)}
                />

                <div>
                  <Button
                    wide
                    type="submit"
                  >
                    {t('button.createDiscipline')}
                  </Button>
                </div>
              </FieldGroup>
            </form>
          </SheetBody>

          <SheetFooter />
        </SheetContent>
      </Sheet>

      <Sheet
        open={editShow}
        onOpenChange={() => {
          setEditPath(false);
          setEditShow(false);
        }}
      >
        <SheetContent>
          <SheetHeader />

          <SheetBody>
            <SheetHeader />

            <SheetBody>
              <form
                method="post"
                className="form"
                onSubmit={e => {
                  e.preventDefault();
                  setEditShow(false);
                  disciplineForm.handleSubmit();
                }}
              >
                <FieldGroup>
                  <SheetTitle>Editar o nome da Disciplina</SheetTitle>

                  <FormField
                    label="Novo nome da Disciplina"
                    value={disciplineForm?.values?.body}
                    type="text"
                    placeholder="Digite o nome da Disciplina"
                    onChange={e => disciplineForm.setFieldValue('body', e.target.value)}
                  />

                  <div>
                    <Button
                      wide
                      type="submit"
                    >
                      Editar e continuar
                    </Button>
                  </div>
                </FieldGroup>
              </form>
            </SheetBody>

            <SheetFooter />
          </SheetBody>

          <SheetFooter />
        </SheetContent>
      </Sheet>
    </ManageSubjects>
  ) : (
    <EmptyState
      type="select"
      title={t('emptyState.titleSelectModule')}
      text={t('emptyState.textSelectModule')}
    />
  );
}
